import type { MapDocument, WorldDocument } from "../document/types.ts";
import { overviewRing, type PixelRect, type RingPosition } from "./map-overview.ts";

/**
 * The maps around the overview's map (ADR-109's amendment of 2026-10-13, decisions 40, 41 and 43, row P1.6-T240): which
 * maps are drawn, where each lies in tiles from the open map's top-left tile, what its plaque says, which of them a
 * view reaches, which one a point is on, and which maps of a world changed. Plain data in whole tiles; nothing reads
 * a layer, and nothing names a game.
 */

/**
 * Whether a neighbour holds nothing, holds layers and is not marked ready, or is ready, as the Maps tab words it.
 */
export type NeighbourState = "blank" | "draft" | "ready";

/**
 * One neighbour of the open map: its rectangle in tiles from the open map's top-left tile (so a map to the west or
 * north has a negative start), the words of its plaque and where it lies around the map.
 */
export interface OverviewNeighbour extends PixelRect {
  readonly map: string;
  readonly name: string;
  readonly region: string | null;
  readonly state: NeighbourState;
  readonly position: RingPosition;
}

/**
 * A point on a neighbour: the neighbour and the tile of it in its own numbering from 0.
 */
export interface NeighbourHit {
  readonly neighbour: OverviewNeighbour;
  readonly tile: { readonly x: number; readonly y: number };
}

/**
 * The corner of a rectangle a plaque is set at.
 */
export interface PlaqueCorner {
  readonly horizontal: "left" | "right";
  readonly vertical: "top" | "bottom";
}

/**
 * The state a map is shown in: blank when it holds no layer, otherwise draft or ready.
 *
 * @param map The map.
 * @returns The state.
 */
function stateOf(map: MapDocument): NeighbourState {
  return map.layers.length === 0 ? "blank" : map.draft === true ? "draft" : "ready";
}

/**
 * The maps drawn around a map: the placed maps that share a stretch of its edge or meet one of its corners, within
 * its depth, from the places alone (the ring of P1.6-T237). A map with no place, a map the world lacks and a world with
 * nothing placed give none.
 *
 * @param world The world.
 * @param mapId The open map.
 * @returns The neighbours clockwise from the north, each with its rectangle in tiles from the open map's top-left tile.
 */
export function overviewNeighbours(world: WorldDocument, mapId: string): OverviewNeighbour[] {
  const own = world.maps.find((m) => m.id === mapId);
  if (own?.place === undefined) {
    return [];
  }
  const origin = own.place;
  const groups = new Map((world.mapGroups ?? []).map((g) => [g.id, g.name]));
  const found: OverviewNeighbour[] = [];
  for (const { map: id, position } of overviewRing(world, mapId)) {
    const other = world.maps.find((m) => m.id === id);
    if (other?.place === undefined) {
      continue;
    }
    found.push({
      map: other.id,
      name: other.name,
      region: other.mapGroup === undefined ? null : (groups.get(other.mapGroup) ?? null),
      state: stateOf(other),
      position,
      x: other.place.x - origin.x,
      y: other.place.y - origin.y,
      width: other.width,
      height: other.height,
    });
  }
  return found;
}

/**
 * The neighbours whose rectangle a view reaches, so a map out of view is neither composed nor drawn.
 *
 * @param neighbours The neighbours.
 * @param view The view in tiles from the open map's top-left tile.
 * @returns The neighbours in view, in their order. A view that only touches a neighbour's edge reaches none of it.
 */
export function neighboursInView(neighbours: readonly OverviewNeighbour[], view: PixelRect): OverviewNeighbour[] {
  return neighbours.filter((n) => n.x < view.x + view.width && view.x < n.x + n.width && n.y < view.y + view.height && view.y < n.y + n.height);
}

/**
 * The neighbour under a point and the tile of it.
 *
 * @param neighbours The neighbours.
 * @param x The point's x in tiles from the open map's top-left tile, fractions allowed.
 * @param y The point's y.
 * @returns The hit, or null when the point is on none (on the open map, on a gap or past an edge).
 */
export function neighbourAt(neighbours: readonly OverviewNeighbour[], x: number, y: number): NeighbourHit | null {
  const hit = neighbours.find((n) => x >= n.x && x < n.x + n.width && y >= n.y && y < n.y + n.height);
  return hit === undefined ? null : { neighbour: hit, tile: { x: Math.floor(x - hit.x), y: Math.floor(y - hit.y) } };
}

/**
 * What the status bar reads over a neighbour: its name and the cell and world pixel under the pointer, each in that
 * map's own numbering from 0 (ADR-109 decision 41).
 */
export interface NeighbourCursor {
  readonly name: string;
  readonly cell: { readonly x: number; readonly y: number };
  readonly pixel: { readonly x: number; readonly y: number };
}

/**
 * The status bar's reading for a point over a neighbour.
 *
 * @param neighbours The neighbours.
 * @param x The point's x in tiles from the open map's top-left tile, fractions allowed.
 * @param y The point's y.
 * @param tileSize The tile size in pixels.
 * @returns The neighbour's name, the cell and the pixel in its own numbering, or null when the point is on no neighbour.
 */
export function neighbourCursor(neighbours: readonly OverviewNeighbour[], x: number, y: number, tileSize: number): NeighbourCursor | null {
  const hit = neighbourAt(neighbours, x, y);
  if (hit === null) {
    return null;
  }
  return { name: hit.neighbour.name, cell: hit.tile, pixel: { x: Math.floor((x - hit.neighbour.x) * tileSize), y: Math.floor((y - hit.neighbour.y) * tileSize) } };
}

/**
 * The corner of a neighbour its plaque is set at: the one that lies nearest the open map, the left one on a side the
 * map lies to the left of, and the top one when both are as near.
 *
 * @param position Where the neighbour lies around the map.
 * @returns The corner.
 */
export function plaqueCorner(position: RingPosition): PlaqueCorner {
  const west = position === "west" || position === "south-west" || position === "north-west";
  const north = position === "north" || position === "north-east" || position === "north-west";
  return { horizontal: west ? "right" : "left", vertical: north ? "bottom" : "top" };
}

/**
 * The maps of a world that differ between two documents: a map whose document object is not the one before, and a
 * map added or removed. The store keeps a map's object while nothing in it changes, so an edit to a neighbour, local or
 * arriving through the live room, names that neighbour.
 *
 * @param before The earlier document.
 * @param after The later document.
 * @returns The ids, those of the later document first in its order, then the removed ones.
 */
export function changedMapIds(before: WorldDocument, after: WorldDocument): string[] {
  const was = new Map(before.maps.map((m) => [m.id, m]));
  const changed = after.maps.filter((m) => was.get(m.id) !== m).map((m) => m.id);
  const now = new Set(after.maps.map((m) => m.id));
  return [...changed, ...before.maps.filter((m) => !now.has(m.id)).map((m) => m.id)];
}
